import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link2, Check, Loader2 } from "lucide-react";
import { API } from "../../utils/helpers";

const SHUBE_LABELS = {
  default: "Ana Sayfa",
  "1": "Ufuktepe",
  "2": "Dutluk",
};

const FiyatKaynagiTab = ({ profile }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState({ source_profile: null, apply_marj: true });
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/price-source?profile=${profile}`);
      setCurrent({
        source_profile: res.data.source_profile,
        apply_marj: res.data.apply_marj !== false,
      });
    } catch (err) {
      console.error("price-source load error:", err);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const saveSource = async (source_profile, apply_marj = true) => {
    if (saving) return;
    setSaving(true);
    setMessage("");
    try {
      await axios.post(`${API}/admin/price-source?profile=${profile}`, {
        source_profile, apply_marj,
      });
      setCurrent({ source_profile, apply_marj });
      const lbl = source_profile === null
        ? "Kendi ayarın"
        : source_profile === "altinkaynak"
          ? "Altınkaynak (Ham)"
          : SHUBE_LABELS[source_profile];
      setMessage(`✓ Kaynak: ${lbl}`);
      setTimeout(() => setMessage(""), 2500);
    } catch (err) {
      console.error("price-source save error:", err);
      setMessage("✗ Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const otherProfiles = Object.keys(SHUBE_LABELS).filter(p => p !== profile);

  const isSelected = (value) => {
    if (value === null) return current.source_profile === null;
    if (value === "altinkaynak") return current.source_profile === "altinkaynak" && current.apply_marj === false;
    return current.source_profile === value;
  };

  const cardStyle = (selected) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", marginBottom: "10px",
    background: selected ? "#334155" : "#1e293b",
    border: `2px solid ${selected ? "#DA9F54" : "#3b4a5f"}`,
    borderRadius: "10px", cursor: saving ? "wait" : "pointer",
    transition: "all 0.2s",
    opacity: saving ? 0.6 : 1,
  });

  const labelStyle = { color: "#DA9F54", fontSize: "15px", fontWeight: "600" };
  const subLabelStyle = { color: "#94a3b8", fontSize: "12px", marginTop: "3px" };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
        <Loader2 size={28} className="animate-spin" style={{ display: "inline-block" }} />
        <div style={{ marginTop: 12 }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: 760, margin: "0 auto" }} data-testid="fiyat-kaynagi-tab">
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "var(--adm-canvas-title)", fontSize: "22px", fontWeight: "bold", margin: 0 }}>
          Fiyat Kaynağı
        </h2>
        <p style={{ color: "var(--adm-canvas-sub)", fontSize: "13px", marginTop: "6px" }}>
          Bu şubenin fiyatları nereden alınsın? Başka bir şube seçersen o şubenin hesaplanmış fiyatları birebir gösterilir.
          "Altınkaynak (Ham)" seçersen herhangi bir marj/çarpan uygulanmadan ham Altınkaynak fiyatları gösterilir.
        </p>
      </div>

      {message && (
        <div style={{
          padding: "10px 14px", marginBottom: "14px",
          background: message.startsWith("✓") ? "#0a3d22" : "#3d1010",
          color: message.startsWith("✓") ? "#22c55e" : "#e57373",
          borderRadius: "8px", fontSize: "13px", fontWeight: "600",
        }}>
          {message}
        </div>
      )}

      {/* Kendi ayarım */}
      <div onClick={() => !saving && saveSource(null)} style={cardStyle(isSelected(null))} data-testid="kaynak-self">
        <div>
          <div style={labelStyle}>Kendi Ayarım (Bu Şube)</div>
          <div style={subLabelStyle}>Bu şubenin kendi mod ve marj ayarları kullanılır</div>
        </div>
        {isSelected(null) && <Check size={22} color="#DA9F54" />}
      </div>

      {/* Diğer şubeler */}
      {otherProfiles.map(p => (
        <div key={p} onClick={() => !saving && saveSource(p)} style={cardStyle(isSelected(p))} data-testid={`kaynak-profile-${p}`}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Link2 size={20} color={isSelected(p) ? "#DA9F54" : "#94a3b8"} />
            <div>
              <div style={labelStyle}>{SHUBE_LABELS[p]}</div>
              <div style={subLabelStyle}>{SHUBE_LABELS[p]} şubesinin fiyatları birebir gösterilir</div>
            </div>
          </div>
          {isSelected(p) && <Check size={22} color="#DA9F54" />}
        </div>
      ))}

      {/* Altınkaynak ham */}
      <div onClick={() => !saving && saveSource("altinkaynak", false)} style={cardStyle(isSelected("altinkaynak"))} data-testid="kaynak-altinkaynak-raw">
        <div>
          <div style={labelStyle}>Altınkaynak (Ham Fiyatlar)</div>
          <div style={subLabelStyle}>Hiçbir marj veya çarpan uygulanmaz · Çeyrek, Yarım, Has vs. birebir Altınkaynak değerleri</div>
        </div>
        {isSelected("altinkaynak") && <Check size={22} color="#DA9F54" />}
      </div>

      {current.source_profile !== null && (
        <div style={{
          marginTop: "24px", padding: "14px 18px",
          background: "#1e293b", border: "1px solid #DA9F54", borderRadius: "10px",
          color: "#DA9F54", fontSize: "13px",
        }}>
          ℹ Bu şube şu an{" "}
          <strong>
            {current.source_profile === "altinkaynak" ? "Altınkaynak (Ham)" : SHUBE_LABELS[current.source_profile]}
          </strong>
          {" "}kaynağına bağlı. Diğer tab'lardaki marj/mod ayarları bu şube için <strong>kullanılmıyor</strong>.
        </div>
      )}
    </div>
  );
};

export default FiyatKaynagiTab;
